// files.tsx — THE FILES OUTFORM SENT US, previewed on the board.
//
// Erez, 26 Aug 2026: "I want to see all the files they gave us previewed so I
// can actually see what's in there. Group them together based on jobs. I want
// to see it like a tile of files previews."
//
// ── WHAT EACH TILE ACTUALLY SHOWS, because the two halves differ and the
//    difference matters ───────────────────────────────────────────────────────
//
//   PDF  — THE REAL FILE. pdf.js renders page 1 in the browser, exactly as
//          <PdfMarkupFrame> already does on this board. Nothing is converted
//          and nothing is pre-rendered; the tile draws the bytes Outform sent.
//
//   XLSX — OUR RENDER of the first visible sheet, not Excel's. He asked for the
//          original form and this is the closest honest thing available: the
//          Tailormind box has no LibreOffice and no PDF rasteriser, so a true
//          screenshot of a workbook cannot be produced here at all. What is
//          drawn is real — values as Excel formats them, real fills, bold,
//          alignment, merges and column widths, straight out of the file — but
//          it is a redraw, and the section says so rather than implying a
//          photograph. Theme and indexed fills have no RGB to read (the known
//          gap from the round-trip measurement), so those cells come out plain
//          instead of guessing a colour.
//
//   MSG / PPTX — no browser can draw either. The tile says what it is and links
//          to the file. Naming the absence beats inventing a preview for it.
//
// ⛔ WHY NOT UNIVER: he ruled it out in the same breath — "I don't want to
// convert it to like a universe thing, I just want to preview it in its
// original form." Univer is also 5.5 MB of grid engine per instance; twenty-nine
// of them on one board is not a thumbnail grid, it is an outage.
//
// ── WHERE THE DATA COMES FROM ───────────────────────────────────────────────
// One previews-v3.json on R2 (530 KB, CORS open), generated by
// scratchpad/outform/extract.py from the real files, plus the 51 originals
// beside it so every tile links to the actual document. The board fetches the
// JSON once; the tiles render from it. No file in this repo, so nothing of the
// client's lands in the bundle — the 20 Aug cross-client leak came from exactly
// that mistake (Vite copies public/ into the Pages deploy verbatim).

import { useEffect, useMemo, useRef, useState } from "react";

const R2 = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/board/outform";
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

type Cell = { r: number; c: number; v: string; b?: number; g?: string; a?: string; k?: string };
type Grid = { sheet: string; rows: number; cols: number; colw: number[]; merges: number[][]; cells: Cell[] };
type FileRec = {
  name: string; slug: string; ext: string; bytes: number; kind: string;
  job: string; client: string; jobDesc: string; url: string;
  sheets?: number; visible?: number; sheetNames?: string[]; visNames?: string[]; cells?: number;
  formulas?: number; pages?: number; grid?: Grid;
  template?: string; templateCount?: number;
};

const KB = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);
const N = (n: number) => n.toLocaleString("en-US");

/** Colour per document KIND — the second thing the eye should pick up after
 *  the shape of the page itself. Same kind, same colour, across every job. */
const KIND_INK: Record<string, string> = {
  "Estimate workbook": "#B45309",
  Breakout: "#7C3AED",
  "BOM export": "#0F766E",
  "PR — spec + renders": "#BE123C",
  Drawing: "#475569",
  Email: "#C2410C",
  "Vendor RFQ": "#C2410C",
  Review: "#4338CA",
  Ballpark: "#A16207",
  "Clarity tool": "#0369A1",
  "Creative brief": "#9333EA",
  Other: "#64748B",
};

// ── PDF TILE ────────────────────────────────────────────────────────────────
// One shared, serialised render queue. Nineteen PDFs each spinning up work at
// once on a 7 GB box is how you turn a preview grid into a stall; they cost
// nothing spread out, because each tile only ever renders page 1.
let queue: Promise<unknown> = Promise.resolve();
const enqueue = <T,>(job: () => Promise<T>): Promise<T> => {
  const next = queue.then(job, job);
  queue = next.catch(() => {});
  return next as Promise<T>;
};

function PdfTile({ url, width, height }: { url: string; width: number; height: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    enqueue(async () => {
      try {
        const pdfjs = await import("pdfjs-dist");
        const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
        pdfjs.GlobalWorkerOptions.workerSrc = (worker as { default: string }).default;
        const doc = await pdfjs.getDocument({ url }).promise;
        const page = await doc.getPage(1);
        const canvas = ref.current;
        if (!live || !canvas) return;
        // Fit by WIDTH and let the tile crop the tail of a tall page — a
        // landscape drawing and a portrait spec sheet then read at the same
        // scale, which is what makes a row of them comparable at a glance.
        const base = page.getViewport({ scale: 1 });
        const vp = page.getViewport({ scale: (width * 2) / base.width });
        canvas.width = Math.ceil(vp.width);
        canvas.height = Math.ceil(vp.height);
        const c2d = canvas.getContext("2d");
        if (!c2d) return;
        await page.render({ canvasContext: c2d, viewport: vp, canvas }).promise;
      } catch {
        if (live) setFailed(true);
      }
    });
    return () => { live = false; };
  }, [url, width]);

  if (failed) return <Placeholder width={width} height={height} label="PDF" note="could not render" />;
  return (
    <canvas
      ref={ref}
      style={{ width, display: "block", background: "#fff" }}
    />
  );
}

// ── WORKBOOK TILE ───────────────────────────────────────────────────────────
function SheetTile({ grid, width, height }: { grid: Grid; width: number; height: number }) {
  const skip = useMemo(() => {
    const s = new Set<string>();
    for (const [r0, c0, rs, cs] of grid.merges)
      for (let r = r0; r < r0 + rs; r++)
        for (let c = c0; c < c0 + cs; c++) if (r !== r0 || c !== c0) s.add(`${r}:${c}`);
    return s;
  }, [grid]);

  const span = useMemo(() => {
    const m = new Map<string, [number, number]>();
    for (const [r0, c0, rs, cs] of grid.merges) m.set(`${r0}:${c0}`, [rs, cs]);
    return m;
  }, [grid]);

  const byPos = useMemo(() => {
    const m = new Map<string, Cell>();
    for (const c of grid.cells) m.set(`${c.r}:${c.c}`, c);
    return m;
  }, [grid]);

  const natural = grid.colw.reduce((a, b) => a + b, 0) || 1;
  const k = width / natural;

  return (
    <div style={{ width, height, overflow: "hidden", background: "#fff", position: "relative" }}>
      <div style={{ transform: `scale(${k})`, transformOrigin: "top left", width: natural }}>
        <table style={{ borderCollapse: "collapse", tableLayout: "fixed", width: natural, fontFamily: FONT }}>
          <colgroup>{grid.colw.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
          <tbody>
            {Array.from({ length: grid.rows }, (_, ri) => {
              const r = ri + 1;
              return (
                <tr key={r} style={{ height: 19 }}>
                  {Array.from({ length: grid.cols }, (_, ci) => {
                    const c = ci + 1;
                    if (skip.has(`${r}:${c}`)) return null;
                    const cell = byPos.get(`${r}:${c}`);
                    const sp = span.get(`${r}:${c}`);
                    return (
                      <td
                        key={c}
                        rowSpan={sp?.[0]}
                        colSpan={sp?.[1]}
                        style={{
                          border: "1px solid #E7E5E4",
                          background: cell?.g,
                          fontWeight: cell?.b ? 700 : 400,
                          textAlign: cell?.a === "c" ? "center" : cell?.a === "r" ? "right" : "left",
                          fontSize: 11,
                          lineHeight: 1.35,
                          padding: "1px 3px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          color: cell?.k ?? "#1C1917",
                        }}
                      >
                        {cell?.v ?? ""}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** ⭐ THE TAB STRIP — added 28 Aug, because the tile was hiding the finding.
 *
 *  Erez asked where the Morphe workbook was. It was on this board the whole
 *  time, and the tile was useless for what he needed: a workbook tile draws
 *  its FIRST VISIBLE SHEET, and the evidence is not on any one sheet — it is
 *  the LIST of sheets. `264714 - Morphe…` carries tabs named `INPUTS -->`,
 *  `Est. Worksheets -->`, `Commercial Output -->`: section dividers Outform
 *  typed themselves, so the tab strip of that file is a process document, and
 *  it matches the competitor's seven-step wizard step for step.
 *
 *  The names were already in the JSON and simply never rendered. Naming the
 *  general shape because it keeps happening: a preview that shows the CONTENT
 *  can still hide the STRUCTURE, and which of the two matters depends on the
 *  question being asked, not on the file.
 *
 *  A `-->` tab is drawn differently on purpose — it is furniture in their
 *  workbook, not a step, and flattening the two would lose exactly what makes
 *  the strip readable as a process. */
function TabStrip({ names, hidden, drawn }: { names: string[]; hidden: number; drawn?: string }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 8 }}>
      {names.map((n) => {
        const divider = /--+>+$/.test(n.trim());
        const on = n === drawn;
        return (
          <span
            key={n}
            title={divider ? `${n} — a section divider, their own` : n}
            style={{
              fontSize: 8.5, lineHeight: 1.5, fontFamily: MONO,
              padding: "1px 4px", borderRadius: 3,
              border: `1px solid ${on ? "#0F766E" : divider ? "#D6D3D1" : "#EDEBE9"}`,
              background: on ? "#0F766E" : divider ? "#FAFAF9" : "#fff",
              color: on ? "#fff" : divider ? "#A8A29E" : "#57534E",
              fontWeight: on ? 700 : divider ? 700 : 400,
              letterSpacing: divider ? "0.04em" : 0,
            }}
          >
            {n}
          </span>
        );
      })}
      {hidden > 0 && (
        <span
          title="Hidden scaffolding sheets — 85% of every cell in this folder lives on these, and nobody opens them"
          style={{
            fontSize: 8.5, lineHeight: 1.5, fontFamily: MONO, padding: "1px 4px",
            borderRadius: 3, border: "1px dashed #D6D3D1", color: "#A8A29E",
          }}
        >
          +{hidden} hidden
        </span>
      )}
    </div>
  );
}

function Placeholder({ width, height, label, note }:
  { width: number; height: number; label: string; note: string }) {
  return (
    <div style={{
      width, height, background: "#FAFAF9", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 6, fontFamily: FONT,
    }}>
      <div style={{ fontSize: 34, fontWeight: 800, color: "#D6D3D1", letterSpacing: "-0.02em" }}>{label}</div>
      <div style={{ fontSize: 11, color: "#A8A29E" }}>{note}</div>
    </div>
  );
}

// ── ONE TILE ────────────────────────────────────────────────────────────────
const TILE_W = 340;
const PREVIEW_H = 300;

function Tile({ f }: { f: FileRec }) {
  const ink = KIND_INK[f.kind] ?? KIND_INK.Other;
  const facts =
    f.ext === "xlsx"
      ? `${f.sheets} sheets · ${f.visible} visible · ${N(f.cells ?? 0)} cells · ${N(f.formulas ?? 0)} formulas`
      : f.ext === "pdf"
        ? `${f.pages} page${f.pages === 1 ? "" : "s"} · ${KB(f.bytes)}`
        : `${KB(f.bytes)}`;

  return (
    // data-zoom: double-click frames THIS tile, so a 3px cell becomes readable
    // without leaving the board. That is BoardCanvas's own gesture, not a new
    // one — and it is why the tile itself is not a click target: an <a> here
    // would fire on the first click of every double-click.
    <div
      data-zoom
      style={{
        width: TILE_W, background: "#fff", border: "1px solid #E7E5E4",
        borderRadius: 10, overflow: "hidden", fontFamily: FONT,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      <div style={{ width: TILE_W, height: PREVIEW_H, overflow: "hidden", background: "#fff",
                    borderBottom: "1px solid #F5F5F4" }}>
        {f.ext === "pdf" ? (
          <PdfTile url={f.url} width={TILE_W} height={PREVIEW_H} />
        ) : f.grid ? (
          <SheetTile grid={f.grid} width={TILE_W} height={PREVIEW_H} />
        ) : (
          <Placeholder
            width={TILE_W} height={PREVIEW_H}
            label={f.ext.toUpperCase()}
            note={f.ext === "msg" ? "Outlook message — open to read" : "no browser preview"}
          />
        )}
      </div>

      <div style={{ padding: "9px 11px 11px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
          <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em",
                         textTransform: "uppercase", color: ink }}>{f.kind}</span>
          {f.template && (f.templateCount ?? 0) > 1 && (
            <span style={{
              fontSize: 9, fontWeight: 700, color: "#57534E", background: "#F5F5F4",
              border: "1px solid #E7E5E4", borderRadius: 4, padding: "1px 5px",
            }}>
              template {f.template} · {f.templateCount}×
            </span>
          )}
        </div>
        <div title={f.name} style={{
          fontSize: 11.5, fontWeight: 600, color: "#1C1917", lineHeight: 1.3,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden", marginBottom: 5, minHeight: 30,
        }}>{f.name}</div>
        <div style={{ fontSize: 9.5, color: "#78716C", fontFamily: MONO, marginBottom: 7 }}>
          {facts}
        </div>
        {/* Every sheet in the file, the drawn one filled in. See TabStrip. */}
        {f.visNames && f.sheets != null && (f.visNames.length > 1 || f.sheets > 1) && (
          <TabStrip names={f.visNames} hidden={f.sheets - f.visNames.length} drawn={f.grid?.sheet} />
        )}
        <a
          className="nopan nodrag"
          href={f.url}
          target="_blank"
          rel="noreferrer"
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            fontSize: 10, fontWeight: 700, color: "#0F766E", textDecoration: "none",
            borderBottom: "1px solid #99F6E4", paddingBottom: 1,
          }}
        >
          Open the original ↗
        </a>
      </div>
    </div>
  );
}

// ── THE SECTION ─────────────────────────────────────────────────────────────
export function OutformFileGrid({ width = 5980 }: { width?: number }) {
  const [data, setData] = useState<FileRec[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch(`${R2}/previews-v3.json`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (live) setData(d.files as FileRec[]); })
      .catch((e) => { if (live) setErr(String(e.message ?? e)); });
    return () => { live = false; };
  }, []);

  const groups = useMemo(() => {
    if (!data) return [];
    const m = new Map<string, FileRec[]>();
    for (const f of data) {
      const k = f.job;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(f);
    }
    // Biggest job first — the folder's own shape, most-documented job leading.
    // Jobs with no number sink to the bottom rather than sorting as "—".
    return [...m.entries()]
      .map(([job, files]) => ({ job, files, client: files[0].client, desc: files[0].jobDesc }))
      .sort((a, b) => (a.job === "—" ? 1 : b.job === "—" ? -1 : b.files.length - a.files.length));
  }, [data]);

  if (err) return <div style={{ width, padding: 40, fontFamily: FONT, fontSize: 22, color: "#B91C1C" }}>
    Could not load the file index: {err}
  </div>;
  if (!data) return <div style={{ width, height: 900, fontFamily: FONT, fontSize: 22, padding: 40, color: "#78716C" }}>
    Loading the 51 files Outform sent…
  </div>;

  return (
    <div style={{ width, fontFamily: FONT }}>
      {/* The one honest sentence this section owes the reader. A workbook tile
          is a redraw; a PDF tile is the file itself. */}
      <div style={{
        display: "flex", gap: 28, alignItems: "baseline", marginBottom: 26,
        paddingBottom: 16, borderBottom: "2px solid #D6D3D1",
      }}>
        <span style={{ fontSize: 30, fontWeight: 800, color: "#1C1917", letterSpacing: "-0.02em" }}>
          51 files · 12 jobs · 5 templates
        </span>
        <span style={{ fontSize: 16, color: "#57534E", lineHeight: 1.4 }}>
          Double-click any tile to read it · <b>PDF tiles are the real file</b>, drawn by pdf.js ·
          workbook tiles are <b>our redraw</b> of the first visible sheet (real values, fills and merges — not a screenshot)
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
        {groups.map((g) => (
          <div key={g.job}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 12 }}>
              <span style={{
                fontSize: 19, fontWeight: 800, color: "#fff", background: "#1C1917",
                borderRadius: 6, padding: "3px 12px", letterSpacing: "-0.01em",
              }}>{g.job === "—" ? "no job number" : g.job}</span>
              <span style={{ fontSize: 19, fontWeight: 700, color: "#1C1917" }}>{g.client}</span>
              <span style={{ fontSize: 15, color: "#78716C" }}>{g.desc}</span>
              <span style={{ fontSize: 13, color: "#A8A29E", fontFamily: MONO }}>
                {g.files.length} file{g.files.length === 1 ? "" : "s"}
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
              {g.files.map((f) => <Tile key={f.slug} f={f} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
